import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  StaffDiaryService,
  DiarySubjectFilter,
  DiaryEntry,
  DiaryDetailData,
  DiaryMediaItem,
  DiaryStudentStatus,
  CreateDiarySubject,
  DiaryReplicateClass,
} from '../services/staff-diary.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;

// customToastr is a global loaded by the surrounding admin shell. If it's
// ever unavailable, calling it directly throws and can abort whatever code
// runs after it — this guards every call site in the component.
function safeToast(message: string, type: string) {
  try {
    if (typeof customToastr === 'function') {
      customToastr(message, type);
    }
  } catch {
    // Ignore — a missing/unavailable toast library should never break the feature.
  }
}

type StudentTab = 'completed' | 'notCompleted';

type DiaryTab = 'Past' | 'Today' | 'Upcoming';

interface EditableMediaItem {
  mediaId: string | null; // null = a new upload, not yet on the server
  type: 'IMAGE' | 'DOCUMENT';
  previewUrl: string;
  file: File | null;
}

@Component({
  selector: 'app-staff-class-diary',
  templateUrl: './staff-class-diary.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-class-diary.component.css']
})
export class StaffClassDiaryComponent implements OnInit {
  classId: string | null;

  tabName: DiaryTab = 'Today';

  loading = false;
  loadError = false;
  loadingMore = false;
  diaryEntries: DiaryEntry[] = [];
  pageNumber = 1;
  maxPageNumber = 1;
  allowCreateDiary = false;
  academicStartDate: string | null = null;
  academicEndDate: string | null = null;

  // "ALL" or a specific "YYYY-MM-DD" date — only meaningful for Past/Upcoming.
  selectedDate: string = 'ALL';
  pickedDate: Date | null = null;

  // Computed once per tab-switch/data-load instead of as template getters —
  // p-calendar treats a new Date object on every change-detection tick as a
  // changed input, which made the picker re-init/jitter while open.
  calendarMinDate: Date | null = null;
  calendarMaxDate: Date | null = null;

  subjectListLoading = false;
  subjectListForFilter: DiarySubjectFilter[] = [];
  selectedSubjectId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffDiaryService: StaffDiaryService,
    private staffSession: StaffSessionService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id');
    this.loadSubjectFilterList();
    this.loadDiary(1);
  }

  private ids(): { staffId: string; classId: string } | null {
    const staffDetails = this.staffSession.getStaffDetails();
    if (!staffDetails || !this.classId) { return null; }
    return { staffId: String(staffDetails.staffId), classId: this.classId };
  }

  async loadSubjectFilterList() {
    const ids = this.ids();
    if (!ids) { return; }
    this.subjectListLoading = true;
    try {
      const response = await firstValueFrom(this.staffDiaryService.getSubjectFilterList(ids.classId, ids.staffId));
      this.subjectListForFilter = response.status ? (response.body?.subjectListForFilter || []) : [];
    } catch {
      this.subjectListForFilter = [];
    } finally {
      this.subjectListLoading = false;
    }
  }

  // ---------- Tab switching ----------
  // Always resets date/subject filters — the mobile app never carries a
  // filter over between tabs.
  setTab(tab: DiaryTab) {
    if (this.tabName === tab) { return; }
    this.tabName = tab;
    this.selectedDate = 'ALL';
    this.pickedDate = null;
    this.selectedSubjectId = null;
    this.showDateSelector = false;
    this.diaryEntries = [];
    this.pageNumber = 1;
    this.recomputeCalendarBounds();
    this.loadDiary(1);
  }

  // ---------- Data fetch (dispatches by tab) ----------
  async loadDiary(page: number) {
    const ids = this.ids();
    if (!ids) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    if (page === 1) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.loadError = false;
    try {
      if (this.tabName === 'Today') {
        const response = await firstValueFrom(
          this.staffDiaryService.getTodaysDiaryData(ids.classId, ids.staffId, page, this.selectedSubjectId)
        );
        this.applyDiaryPage(page, response.status, response.body?.diaryDetailsForTodaysDueDates, response.body);
      } else if (this.tabName === 'Past') {
        const response = await firstValueFrom(
          this.staffDiaryService.getPastDiaryData(ids.classId, ids.staffId, page, this.selectedDate, this.selectedSubjectId)
        );
        this.applyDiaryPage(page, response.status, response.body?.diaryDetailsForPastDueDates, response.body);
      } else {
        const response = await firstValueFrom(
          this.staffDiaryService.getUpcomingDiaryData(ids.classId, ids.staffId, page, this.selectedDate, this.selectedSubjectId)
        );
        // Fixed vs. the mobile app: appends like the other two tabs instead
        // of replacing (replacing would discard already-loaded pages).
        this.applyDiaryPage(page, response.status, response.body?.diaryDetailsForUpcomingDueDates, response.body);
      }
    } catch (error) {
      // Fixed vs. the mobile app: Past's error path there leaves loadingData
      // stuck true — here every tab correctly clears loading on failure.
      this.loadError = true;
    } finally {
      this.loading = false;
      this.loadingMore = false;
    }
  }

  private applyDiaryPage(
    page: number,
    status: boolean,
    entries: DiaryEntry[] | undefined,
    body: { isStaffAllowedToCreateDiary: boolean; academicStartDate: string; academicEndDate: string; maxPageNumber: number } | undefined
  ) {
    if (status && body) {
      const list = entries || [];
      this.diaryEntries = page === 1 ? list : [...this.diaryEntries, ...list];
      this.allowCreateDiary = body.isStaffAllowedToCreateDiary;
      this.academicStartDate = body.academicStartDate;
      this.academicEndDate = body.academicEndDate;
      this.maxPageNumber = body.maxPageNumber || 1;
      this.pageNumber = page;
      this.recomputeCalendarBounds();
    } else if (page === 1) {
      this.diaryEntries = [];
    }
    this.updateDateSelectorVisibility();
  }

  loadMore() {
    if (this.diaryEntries.length > 10 && this.maxPageNumber > this.pageNumber && !this.loadingMore) {
      this.loadDiary(this.pageNumber + 1);
    }
  }

  // ---------- Subject filter ----------
  selectSubject(subjectId: number | null) {
    if (this.selectedSubjectId === subjectId) { return; }
    this.selectedSubjectId = subjectId;
    this.loadDiary(1);
  }

  // ---------- Date filter (Past / Upcoming only) ----------
  // Kept visible once it has ever had data for this tab, so the picker
  // doesn't flicker away and reappear while a new page is loading. Plain
  // field (not a getter) recomputed explicitly after each fetch, so
  // p-calendar's inputs stay referentially stable between change-detection
  // cycles instead of being touched every tick.
  showDateSelector = false;

  private updateDateSelectorVisibility() {
    if (this.tabName === 'Today') {
      this.showDateSelector = false;
      return;
    }
    if (this.diaryEntries.length > 0) {
      this.showDateSelector = true;
    }
  }

  selectAllDates() {
    this.pickedDate = null;
    if (this.selectedDate === 'ALL') { return; }
    this.selectedDate = 'ALL';
    this.loadDiary(1);
  }

  onDatePicked() {
    if (!this.pickedDate) { return; }
    const dateStr = this.toDateString(this.pickedDate);
    if (this.selectedDate === dateStr) { return; }
    this.selectedDate = dateStr;
    this.loadDiary(1);
  }

  private toDateString(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  // Past: can't pick today or a future date. Upcoming: can't pick today or
  // a past date. Falls back to the API's academic year bounds otherwise.
  private recomputeCalendarBounds() {
    if (this.tabName === 'Upcoming') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      this.calendarMinDate = tomorrow;
      this.calendarMaxDate = this.academicEndDate ? new Date(this.academicEndDate) : null;
    } else if (this.tabName === 'Past') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      this.calendarMinDate = this.academicStartDate ? new Date(this.academicStartDate) : null;
      this.calendarMaxDate = yesterday;
    } else {
      this.calendarMinDate = null;
      this.calendarMaxDate = null;
    }
  }


  formatDate(value: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) { return value; }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // ---------- Diary detail side drawer (DiaryDetailedView) ----------
  showDetailDrawer = false;
  detailLoading = false;
  detailLoadError = false;
  detailSubject = '';
  diaryDetail: DiaryDetailData | null = null;
  imagesArray: DiaryMediaItem[] = [];
  pdfArray: DiaryMediaItem[] = [];

  showStudentSheet = false;
  studentTab: StudentTab = 'notCompleted';
  studentSearchQuery = '';
  filteredStudents: DiaryStudentStatus[] = [];

  showImageViewer = false;
  imageViewerIndex = 0;

  showPdfViewer = false;
  pdfSafeUrl: SafeResourceUrl | null = null;
  pdfViewerUrl: string | null = null;

  async openDiaryEntry(entry: DiaryEntry) {
    const ids = this.ids();
    if (!ids) { return; }

    this.detailSubject = entry.subject;
    this.showDetailDrawer = true;
    this.detailLoading = true;
    this.detailLoadError = false;
    this.diaryDetail = null;
    this.imagesArray = [];
    this.pdfArray = [];

    try {
      const response = await firstValueFrom(
        this.staffDiaryService.getDiaryDetails(ids.classId, entry.diaryId, entry.subjectId, ids.staffId)
      );
      if (response.status && response.body?.diaryData) {
        this.diaryDetail = response.body.diaryData;
        const media = this.diaryDetail.diaryMediaDetails || [];
        this.imagesArray = media.filter(m => m.DiaryMediaType === 'IMAGE');
        this.pdfArray = media.filter(m => m.DiaryMediaType === 'DOCUMENT');
      } else {
        this.detailLoadError = true;
      }
    } catch (error) {
      this.detailLoadError = true;
    } finally {
      this.detailLoading = false;
    }
  }

  closeDetailDrawer() {
    this.showDetailDrawer = false;
    this.diaryDetail = null;
  }

  get diaryDetailInfo() {
    return this.diaryDetail?.diaryDetails?.[0] || null;
  }

  formatDuration(minutes: number): string {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0 && mins > 0) { return `${hrs}h ${mins}m`; }
    if (hrs > 0) { return `${hrs}h`; }
    return `${mins}m`;
  }

  // ---------- Image gallery ----------
  openImageViewer(index: number) {
    this.imageViewerIndex = index;
    this.showImageViewer = true;
  }

  closeImageViewer() {
    this.showImageViewer = false;
  }

  imageViewerPrev(event?: Event) {
    event?.stopPropagation();
    if (this.imageViewerIndex > 0) { this.imageViewerIndex--; }
  }

  imageViewerNext(event?: Event) {
    event?.stopPropagation();
    if (this.imageViewerIndex < this.imagesArray.length - 1) { this.imageViewerIndex++; }
  }

  // ---------- PDF viewer ----------
  openPdfViewer(item: DiaryMediaItem) {
    this.pdfViewerUrl = item.DiaryMediaUrl;
    this.pdfSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(item.DiaryMediaUrl);
    this.showPdfViewer = true;
  }

  closePdfViewer() {
    this.showPdfViewer = false;
    this.pdfSafeUrl = null;
    this.pdfViewerUrl = null;
  }

  downloadAttachment(url: string | null) {
    if (!url) { return; }
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.detailSubject}Diary`;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ---------- Student completion bottom sheet ----------
  openStudentSheet(tab: StudentTab) {
    this.studentTab = tab;
    this.studentSearchQuery = '';
    this.showStudentSheet = true;
    this.applyStudentFilter();
  }

  closeStudentSheet() {
    this.showStudentSheet = false;
  }

  setStudentTab(tab: StudentTab) {
    if (this.studentTab === tab) { return; }
    this.studentTab = tab;
    this.studentSearchQuery = '';
    this.applyStudentFilter();
  }

  onStudentSearchChange() {
    this.applyStudentFilter();
  }

  private applyStudentFilter() {
    const list = this.studentTab === 'completed'
      ? (this.diaryDetail?.diaryCompletedStudentDetails || [])
      : (this.diaryDetail?.diaryNotCompletedStudentDetails || []);

    const query = this.studentSearchQuery.trim().toLowerCase();
    if (!query) {
      this.filteredStudents = list;
      return;
    }

    // Fixed vs. the mobile app: a query matching nothing there silently
    // falls back to the full list — here it correctly shows an empty state.
    this.filteredStudents = this.studentTab === 'completed'
      ? list.filter(s => `${s.classNumber} ${s.classDivision} ${s.classTeacherName}`.toLowerCase().includes(query))
      : list.filter(s => `${s.studentName} ${s.rollNumber}`.toLowerCase().includes(query));
  }

  goBack() {
    this.router.navigate(['/staff/class', this.classId]);
  }

  // ---------- Create / Edit Diary form drawer ----------
  showFormDrawer = false;
  formMode: 'create' | 'edit' = 'create';
  formSaving = false;

  formSubjectId: number | null = null;
  formSubjectLocked = false; // subject is read-only when editing
  formSubjectOptions: CreateDiarySubject[] = [];
  formSubjectListLoading = false;

  formTitle = '';
  formDescription = '';
  formDurationHours: number | null = null;
  formDurationMinutes: number | null = null;
  formDate: Date | null = null;
  formDateMin: Date | null = null;
  formDateMax: Date | null = null;

  formClassOptions: DiaryReplicateClass[] = [];
  formClassListLoading = false;
  formSelectedClassIds: number[] = [];

  formMedia: EditableMediaItem[] = [];
  formDeletedMediaIds: string[] = [];

  private formEditingDiaryId: string | null = null;

  openCreateDiaryForm() {
    this.formMode = 'create';
    this.formSubjectLocked = false;
    this.formEditingDiaryId = null;
    this.formSubjectId = null;
    this.formTitle = '';
    this.formDescription = '';
    this.formDurationHours = null;
    this.formDurationMinutes = null;
    this.formDate = null;
    this.formSelectedClassIds = [];
    this.formClassOptions = [];
    this.formMedia = [];
    this.formDeletedMediaIds = [];

    // Can't create a diary for a past date — bounded to the class's
    // academic year end, same as the mobile app's route-param bounds.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.formDateMin = today;
    this.formDateMax = this.academicEndDate ? new Date(this.academicEndDate) : null;

    this.showFormDrawer = true;
    this.loadFormSubjectList();
  }

  openEditDiaryForm() {
    if (!this.diaryDetail || !this.diaryDetailInfo) { return; }
    const entry = this.diaryEntries.find(e => e.subject === this.detailSubject);

    this.formMode = 'edit';
    this.formSubjectLocked = true;
    this.formEditingDiaryId = entry?.diaryId || null;
    this.formSubjectId = entry ? Number(entry.subjectId) : null;
    this.formSubjectOptions = entry ? [{ subjectId: Number(entry.subjectId), subject: entry.subject }] : [];

    const info = this.diaryDetailInfo;
    this.formTitle = info.diaryTitle || '';
    this.formDescription = info.diaryDescription || '';
    const duration = info.diaryDuration || 0;
    this.formDurationHours = Math.floor(duration / 60);
    this.formDurationMinutes = duration % 60;

    const parsedDate = new Date(info.submissionDate);
    this.formDate = isNaN(parsedDate.getTime()) ? null : parsedDate;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.formDateMin = today;
    // Mobile app sources this from diaryInfo[0].EndDate, a field that
    // doesn't actually appear in the documented getDiaryDetails response —
    // falls back to the class's academic end date here instead of leaving
    // the calendar with no effective upper bound.
    this.formDateMax = this.academicEndDate ? new Date(this.academicEndDate) : null;

    this.formSelectedClassIds = [];
    this.formClassOptions = [];

    this.formMedia = (this.diaryDetail.diaryMediaDetails || []).map(m => ({
      mediaId: (m as any).pkDiaryMediaId ? String((m as any).pkDiaryMediaId) : null,
      type: m.DiaryMediaType,
      previewUrl: m.DiaryMediaType === 'IMAGE' ? m.DiaryMediaBlurUrl : m.DiaryMediaUrl,
      file: null,
    }));
    this.formDeletedMediaIds = [];

    this.showFormDrawer = true;
    this.closeDetailDrawer();
  }

  closeFormDrawer() {
    this.formMedia.forEach(m => { if (m.file) { URL.revokeObjectURL(m.previewUrl); } });
    this.showFormDrawer = false;
  }

  private async loadFormSubjectList() {
    const ids = this.ids();
    if (!ids) { return; }
    this.formSubjectListLoading = true;
    try {
      const response = await firstValueFrom(this.staffDiaryService.getSubjectListForCreateDiary(ids.classId, ids.staffId));
      this.formSubjectOptions = response.status ? (response.body?.subjectListForCreateDiaryData || []) : [];
    } catch {
      this.formSubjectOptions = [];
    } finally {
      this.formSubjectListLoading = false;
    }
  }

  // Re-fires whenever the subject changes — the "add other classes" list is
  // scoped to which other class sections also teach the selected subject.
  async onFormSubjectChange() {
    this.formSelectedClassIds = [];
    this.formClassOptions = [];
    const ids = this.ids();
    if (!ids || this.formSubjectId === null) { return; }
    this.formClassListLoading = true;
    try {
      const response = await firstValueFrom(this.staffDiaryService.getClassListForDiary(ids.classId, this.formSubjectId, ids.staffId));
      this.formClassOptions = response.status ? (response.body?.classListForReplicatingDiaryData || []) : [];
    } catch {
      this.formClassOptions = [];
    } finally {
      this.formClassListLoading = false;
    }
  }

  toggleFormClass(teachingClassId: number) {
    const idx = this.formSelectedClassIds.indexOf(teachingClassId);
    if (idx >= 0) {
      this.formSelectedClassIds.splice(idx, 1);
    } else {
      this.formSelectedClassIds.push(teachingClassId);
    }
  }

  classLabel(c: DiaryReplicateClass): string {
    return `${c.classNumber} - ${c.classDivision}`;
  }

  onFormFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) { return; }
    Array.from(input.files).forEach(file => {
      const isPdf = file.type === 'application/pdf';
      this.formMedia.push({
        mediaId: null,
        type: isPdf ? 'DOCUMENT' : 'IMAGE',
        previewUrl: isPdf ? '' : URL.createObjectURL(file),
        file,
      });
    });
    input.value = '';
  }

  removeFormMedia(index: number) {
    const item = this.formMedia[index];
    if (item.file) {
      URL.revokeObjectURL(item.previewUrl);
    } else if (item.mediaId) {
      // A pre-existing server file being removed on Edit — queue its ID for
      // deletedMediaIdList. Removing a not-yet-uploaded local file needs no
      // server-side delete call at all.
      this.formDeletedMediaIds.push(item.mediaId);
    }
    this.formMedia.splice(index, 1);
  }

  private toDateOnlyString(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  async submitDiaryForm() {
    const ids = this.ids();
    if (!ids) { return; }

    if (this.formMode === 'create') {
      if (this.formSubjectId === null) { safeToast(await firstValueFrom(this.translate.get('pleaseSelectASubject')), 'error'); return; }
      if (!this.formTitle.trim() || this.formTitle.trim().length <= 3) { safeToast(await firstValueFrom(this.translate.get('pleaseEnterAValidTitle')), 'error'); return; }
      if (!this.formDescription.trim() || this.formDescription.trim().length <= 3) { safeToast(await firstValueFrom(this.translate.get('pleaseEnterAValidDescription')), 'error'); return; }
      if (!this.formDate) { safeToast(await firstValueFrom(this.translate.get('pleaseSelectASubmissionDate')), 'error'); return; }
    } else {
      // Edit's validation is looser — only blocks if BOTH the new value and
      // the original are empty (matches the mobile app exactly).
      const info = this.diaryDetailInfo;
      if (!this.formTitle.trim() && !info?.diaryTitle) { safeToast(await firstValueFrom(this.translate.get('pleaseEnterATitle')), 'error'); return; }
      if (!this.formDescription.trim() && !info?.diaryDescription) { safeToast(await firstValueFrom(this.translate.get('pleaseEnterADescription')), 'error'); return; }
      if (!this.formDate && !info?.submissionDate) { safeToast(await firstValueFrom(this.translate.get('pleaseSelectASubmissionDate')), 'error'); return; }
    }

    const durationMinutes = (this.formDurationHours || 0) * 60 + (this.formDurationMinutes || 0);
    const newFiles = this.formMedia.filter(m => m.file).map(m => m.file as File);

    this.formSaving = true;
    try {
      if (this.formMode === 'create') {
        const classIdList = [...this.formSelectedClassIds.map(String), ids.classId];
        const response = await firstValueFrom(this.staffDiaryService.createDiary({
          staffId: ids.staffId,
          subjectId: String(this.formSubjectId),
          diaryTitle: this.formTitle.trim(),
          diaryDescription: this.formDescription.trim(),
          diaryEstimateTime: durationMinutes,
          diarySubmissionDate: this.toDateOnlyString(this.formDate!),
          classIdList,
          lang: 'en',
        }, newFiles));
        if (response.status) {
          const today = this.toDateOnlyString(new Date());
          const isToday = this.toDateOnlyString(this.formDate!) === today;
          safeToast(await firstValueFrom(this.translate.get(isToday ? 'diaryCreatedForToday' : 'diaryScheduledSuccessfully')), 'success');
          this.closeFormDrawer();
          this.loadDiary(1);
        } else {
          safeToast(response.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
        }
      } else {
        if (!this.formEditingDiaryId) { return; }
        const info = this.diaryDetailInfo;
        const response = await firstValueFrom(this.staffDiaryService.editDiary({
          diaryId: this.formEditingDiaryId,
          staffId: ids.staffId,
          subjectId: String(this.formSubjectId),
          diaryTitle: this.formTitle.trim() || info?.diaryTitle || '',
          diaryDescription: this.formDescription.trim() || info?.diaryDescription || '',
          deletedMediaIdList: this.formDeletedMediaIds,
          diaryEstimateTime: durationMinutes || info?.diaryDuration || 0,
          diarySubmissionDate: this.formDate ? this.toDateOnlyString(this.formDate) : (info?.submissionDate || ''),
          classId: ids.classId,
          lang: 'en',
        }, newFiles));
        if (response.status) {
          safeToast(await firstValueFrom(this.translate.get('diaryUpdated')), 'success');
          this.closeFormDrawer();
          this.loadDiary(1);
        } else {
          safeToast(response.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
        }
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    } finally {
      this.formSaving = false;
    }
  }
}
